import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import React, { useMemo, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { DemeterPageElementType, DemeterPageType, DemeterUserType } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import useDashboardsApi from '../../../Apis/Hooks/useDashboardsApiHook';
import usePresentationTemplatesApi from '../../../Apis/Hooks/usePresentationTemplatesApiHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import usePageElementForComponent from '../../Navigation/Hooks/usePageElementForComponentHook';
import IconMenuMultiStageDropdown, { INestedOption } from './IconMenuMultiStageDropdown';
import styles from './Inputs.module.scss';

export enum ComponentHeaderRightSideOptions {
    ChartPopout = 'chartPopout',
    ChartDownload = 'chartDownload',
    RenameWidget = 'renameWidget',
    CloneWidget = 'cloneWidget',
    DeleteWidget = 'deleteWidget',
    Jpeg = 'jpeg',
    Svg = 'svg',
    Pdf = 'pdf',
    Png = 'png',
    Csv = 'csv',
    Xlsx = 'xlsx',
    Maximize = 'maximize',
    Minimize = 'minimize',
    ValueCsv = 'valueCsv',
    ValueXlsx = 'valueXlsx',
    PercentCsv = 'percentCsv',
    PercentXlsx = 'percentXlsx',
    AddToDashboard = 'addToDashboard',
    AddToPresentationTemplate = 'addToPresentationTemplate',
}

interface IComponentHeaderRightSideActions {
    name: string; // Internal name of the component.
    handleMoreOptionsSelection: (value: ComponentHeaderRightSideOptions) => void;
    isInModal?: boolean;
    isInDashboardEditMode?: boolean;
    isCsvDownloadAvailable?: boolean;
    isXlsxDownloadAvailable?: boolean;
    ignoreDefaultDownloadOptions?: boolean;
    hidePopoutModal?: boolean;
    hideDownload?: boolean;
    maximizeMinimizeButton?: ComponentHeaderRightSideOptions.Maximize | ComponentHeaderRightSideOptions.Minimize;
    pageType?: DemeterPageType;
    pageElementType?: DemeterPageElementType;
    testId?: string | undefined;
    track: TrackingEvent;
}

const addToDashboardOptionKey = 'addToDashboard_';
const addToPresentationTemplateOptionKey = 'addToPresentationTemplate_';

const ComponentHeaderRightSideActions: React.FC<IComponentHeaderRightSideActions> = (props: IComponentHeaderRightSideActions) => {
    const [translations] = useLanguage();
    const [maximizeMinimizeButton, setMaximizeMinimizeButton] = useState<
        ComponentHeaderRightSideOptions.Maximize | ComponentHeaderRightSideOptions.Minimize | undefined
    >(props.maximizeMinimizeButton);
    const currentUserType = useApplicationSelector(selectUserType);

    const isAdministrator = currentUserType === DemeterUserType.Administrator;

    const pageElementForComponent = usePageElementForComponent({
        pageType: props.pageType,
        pageElementType: props.pageElementType,
    });

    const { dashboards, addPageElementToDashboard, isLoadingDashboards, isUpsertDemeterDashboardLoading } = useDashboardsApi();
    const { presentationTemplates, addPageElementToPresentationTemplate, isLoadingPresentationTemplates, isUpsertPresentationTemplateLoading } =
        usePresentationTemplatesApi();

    const componentMenuOptions = useMemo(() => {
        const menuOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];

        if (!props.hideDownload) {
            const downloadOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];

            if (!props.ignoreDefaultDownloadOptions) {
                downloadOptions.push(
                    {
                        key: ComponentHeaderRightSideOptions.Jpeg,
                        label: translations.dropdown.downloads.jpg.toLocaleUpperCase(),
                        value: ComponentHeaderRightSideOptions.Jpeg,
                    },
                    {
                        key: ComponentHeaderRightSideOptions.Svg,
                        label: translations.dropdown.downloads.svg.toLocaleUpperCase(),
                        value: ComponentHeaderRightSideOptions.Svg,
                    },
                    {
                        key: ComponentHeaderRightSideOptions.Pdf,
                        label: translations.dropdown.downloads.pdf.toLocaleUpperCase(),
                        value: ComponentHeaderRightSideOptions.Pdf,
                    },
                );

                if (isAdministrator) {
                    downloadOptions.push({
                        key: ComponentHeaderRightSideOptions.Png,
                        label: translations.dropdown.downloads.png.toLocaleUpperCase(),
                        value: ComponentHeaderRightSideOptions.Png,
                    });
                }
            }

            if (props.isXlsxDownloadAvailable) {
                downloadOptions.push({
                    key: ComponentHeaderRightSideOptions.Xlsx,
                    label: translations.dropdown.downloads.xlsx.toLocaleUpperCase(),
                    value: ComponentHeaderRightSideOptions.Xlsx,
                });
            }

            if (props.isCsvDownloadAvailable) {
                downloadOptions.push({
                    key: ComponentHeaderRightSideOptions.Csv,
                    label: translations.dropdown.downloads.csv.toLocaleUpperCase(),
                    value: ComponentHeaderRightSideOptions.Csv,
                });
            }

            menuOptions.push({
                key: ComponentHeaderRightSideOptions.ChartDownload,
                label: (
                    <div className={styles.component_more_options_label_container}>
                        <div>{translations.dropdown.downloadAs}</div>
                    </div>
                ),
                value: ComponentHeaderRightSideOptions.ChartDownload,
                options: downloadOptions,
            });
        }

        if (!props.hidePopoutModal && !props.isInModal) {
            menuOptions.push({
                key: ComponentHeaderRightSideOptions.ChartPopout,
                label: (
                    <div className={styles.component_more_options_label_container}>
                        <div>{translations.dropdown.expandToFullScreen}</div>
                    </div>
                ),
                value: ComponentHeaderRightSideOptions.ChartPopout,
                options: [],
            });
        }

        if (props.isInDashboardEditMode) {
            menuOptions.push(
                {
                    key: ComponentHeaderRightSideOptions.RenameWidget,
                    label: translations.dashboardCustomization.widget.renameWidget,
                    value: ComponentHeaderRightSideOptions.RenameWidget,
                },
                {
                    key: ComponentHeaderRightSideOptions.CloneWidget,
                    label: translations.dashboardCustomization.widget.cloneWidget,
                    value: ComponentHeaderRightSideOptions.CloneWidget,
                },
                {
                    key: ComponentHeaderRightSideOptions.DeleteWidget,
                    label: translations.dashboardCustomization.widget.deleteWidget,
                    value: ComponentHeaderRightSideOptions.DeleteWidget,
                },
            );
        }

        if (pageElementForComponent) {
            const isDashboardLoading = isLoadingDashboards || isUpsertDemeterDashboardLoading;
            const dashboardSubOptions: INestedOption<ComponentHeaderRightSideOptions>[] = dashboards.map((dashboard) => ({
                key: `${addToDashboardOptionKey}${dashboard.demeterDashboardGuid}`,
                label: dashboard.name,
                value: `${addToDashboardOptionKey}${dashboard.demeterDashboardGuid}` as ComponentHeaderRightSideOptions,
                disabled: isDashboardLoading,
            }));

            const isPresentationTemplateLoading = isLoadingPresentationTemplates || isUpsertPresentationTemplateLoading;
            const presentationTemplateSubOptions: INestedOption<ComponentHeaderRightSideOptions>[] = presentationTemplates.map((presentationTemplate) => ({
                key: `${addToPresentationTemplateOptionKey}${presentationTemplate.presentationTemplateGuid}`,
                label: presentationTemplate.name,
                value: `${addToPresentationTemplateOptionKey}${presentationTemplate.presentationTemplateGuid}` as ComponentHeaderRightSideOptions,
                disabled: isPresentationTemplateLoading,
            }));

            menuOptions.push(
                {
                    key: ComponentHeaderRightSideOptions.AddToDashboard,
                    label: translations.dashboardCustomization.labels.addToDashboard,
                    value: ComponentHeaderRightSideOptions.AddToDashboard,
                    options: dashboardSubOptions,
                    disabled: isDashboardLoading,
                },
                {
                    key: ComponentHeaderRightSideOptions.AddToPresentationTemplate,
                    label: translations.presentationTemplates.actions.addToPresentationTemplate,
                    value: ComponentHeaderRightSideOptions.AddToPresentationTemplate,
                    options: presentationTemplateSubOptions,
                    disabled: isPresentationTemplateLoading,
                },
            );
        }

        return menuOptions;
    }, [
        props,
        pageElementForComponent,
        dashboards,
        presentationTemplates,
        translations,
        isLoadingDashboards,
        isUpsertDemeterDashboardLoading,
        isLoadingPresentationTemplates,
        isUpsertPresentationTemplateLoading,
    ]);

    const handleSelection = (option: ComponentHeaderRightSideOptions) => {
        if (option.toString().startsWith(addToDashboardOptionKey)) {
            const dashboardGuid = option.toString().replace(addToDashboardOptionKey, '');
            if (pageElementForComponent) {
                addPageElementToDashboard(dashboardGuid, pageElementForComponent);
            }
            return;
        }

        if (option.toString().startsWith(addToPresentationTemplateOptionKey)) {
            const presentationTemplateGuid = option.toString().replace(addToPresentationTemplateOptionKey, '');
            if (pageElementForComponent) {
                addPageElementToPresentationTemplate(presentationTemplateGuid, pageElementForComponent);
            }
            return;
        }

        props.handleMoreOptionsSelection(option);
    };

    const handleMaximizeMinimize = () => {
        if (!props.maximizeMinimizeButton) {
            return;
        }

        props.handleMoreOptionsSelection(
            maximizeMinimizeButton === ComponentHeaderRightSideOptions.Maximize
                ? ComponentHeaderRightSideOptions.Maximize
                : ComponentHeaderRightSideOptions.Minimize,
        );

        // eslint-disable-next-line no-confusing-arrow
        setMaximizeMinimizeButton((previous) =>
            previous === ComponentHeaderRightSideOptions.Maximize ? ComponentHeaderRightSideOptions.Minimize : ComponentHeaderRightSideOptions.Maximize,
        );
    };

    return (
        <div className={styles.component_more_options_actions}>
            <div className={!props.isInDashboardEditMode ? styles.component_more_options_actions_dropdown : ''}>
                <IconMenuMultiStageDropdown
                    name={props.name}
                    tooltip={translations.dropdown.moreOptions}
                    iconButton={<MoreHorizOutlinedIcon sx={{ color: scssVariables.mainTextColor }} />}
                    handleSelection={handleSelection}
                    options={componentMenuOptions}
                    testId={props.testId}
                    track={{
                        eventCategory: props.track.eventCategory,
                        eventAction: `${props.track.eventAction}MoreOptions`,
                    }}
                />
            </div>
            {props.maximizeMinimizeButton && (
                <button
                    type="button"
                    data-testid={props.testId}
                    className={styles.component_more_options_maximize_minimize_button}
                    onClick={handleMaximizeMinimize}
                >
                    {maximizeMinimizeButton === ComponentHeaderRightSideOptions.Maximize && <div>{translations.dashboardCustomization.text.maximize}</div>}
                    {maximizeMinimizeButton === ComponentHeaderRightSideOptions.Minimize && <div>{translations.dashboardCustomization.text.minimize}</div>}
                    <SwapHorizIcon sx={{ color: scssVariables.plainWhite }} />
                </button>
            )}
        </div>
    );
};

export default ComponentHeaderRightSideActions;
